import React from 'react';
import Style from './index.less';
import Slider from '@tencent/dui/lib/components/Slider';
import NumberInput from '../number-input';
import classNames from 'classnames';
import { NUMBER_SLIDE_SIZE } from '../constants';

export interface NumberSlideBarProps {
    type: string;  // {滑动条属性,e.g. opacity, size, .etc}
    title: string; // {滑动条名字,e.g. 透明度，大小等}
    // onValueChange: func;
    maxLength: number;
    postFix: string;
    toFixed: number;
    value: number|string;
    sliderUpperLimit?: number; // 滑动条的最大值
    upperLimit: number;
    lowerLimit: number;
    slideBarStep: number;
    numberInputStep: number;
    allowDecimal: boolean;
    disabled: boolean;
    onPreviewEnd?: () => void;
    onValueEnter: (e) => void;
    onValueLeave: (e) => void;
    onValueChange: (type, value, fromCustom?, changing?) => void;
    sliderStyle?: object;
    sliderContainerStyle?: object;
    size?: string; // 大小， 'default' | 'small' | 'large'
    onFocus: (e?) => void;
    onBlur: () => void;
    canShowEmptyString: boolean;
    isIntegerOfStep?: boolean; // 数值是否展示为步长的整数倍
}

interface NumberSlideBarState {
    valueNum: number;
    valueStr: string;
}

/**
 * 带有数字显示的滑动条
 * {滑动条名字} {滑动条本身} {数字输入框}
 */
export default class NumberSlideBar extends React.Component<NumberSlideBarProps, NumberSlideBarState> {
    public static defaultProps = {
        maxLength: 50,
        postFix: '%',
        toFixed: 0,
        value: 0,
        upperLimit: 100,
        lowerLimit: 0,
        slideBarStep: 1,
        numberInputStep: 1,
        allowDecimal: false,
        disabled: false,
    };

    private postFix: string;
    private type: string;
    private numberInputRef: NumberInput|null;
    public constructor(props: NumberSlideBarProps, context) {
        super(props, context);

        this.postFix = props.postFix;
        this.type = props.type;
        const v = this.handleLimitBoundary(props.value);
        this.state = {
            valueNum: v,
            valueStr: `${v}${this.postFix}`,
        };
    }

    // eslint-disable-next-line react/no-deprecated
    public componentWillReceiveProps(nextProps) {
        if (nextProps.value !== this.state.valueNum) {
            const v = this.handleLimitBoundary(nextProps.value);
            this.setNewNumSlideBarState(v);
        }
    }

    public render() {
        const {
            title,
            upperLimit,
            sliderUpperLimit,
            lowerLimit,
            slideBarStep,
            numberInputStep,
            maxLength,
            toFixed,
            allowDecimal,
            disabled,
            sliderStyle,
            size,
            sliderContainerStyle,
        } = this.props;
        // eslint-disable-next-line no-nested-ternary
        const style = !title ? { display: 'none' } : (disabled ? { color: 'rgba(0, 0, 0, 0.16)' } : undefined);
        const isSmall = size === NUMBER_SLIDE_SIZE.SMALL;
        const sliderClass = classNames(Style.slider, { [Style['slider-small']]: isSmall });
        const sliderContainer = classNames(Style['slidebar-container'], { [Style['slidebar-container-small']]: isSmall });

        return (
            <div className={sliderContainer} onMouseLeave={this.props.onValueLeave}
                onMouseEnter={this.props.onValueEnter} style={sliderContainerStyle}>
                <div className={Style['item-text']} style={style}>{title}</div>
                <Slider disabled={disabled}
                    className={sliderClass}
                    style={sliderStyle}
                    min={lowerLimit}
                    max={sliderUpperLimit || upperLimit}
                    step={slideBarStep}
                    value={this.state.valueNum}
                    onChange={this.onSlideBarChanging}
                    onChangeEnd={this.onSlideBarChangeEnd} />

                <NumberInput disabled={disabled}
                    style={{ marginLeft: '5px', width: '' }}
                    size={size}
                    onValueChange={this.onNumberInputChange}
                    value={this.state.valueStr}
                    valuePostFix={this.postFix}
                    step={numberInputStep}
                    maxLength={maxLength}
                    toFixed={toFixed}
                    min={lowerLimit}
                    max={upperLimit}
                    allowDecimal={allowDecimal}
                    onFocus={this.props.onFocus}
                    onBlur={this.handleBlur}
                    ref={(ref) => {
                        this.numberInputRef = ref;
                    }}
                />
            </div>
        );
    }

    private handleLimitBoundary = (val) => {
        if (this.checkIsEmptyValue(val)) return val;
        // 加一下 val !== 0 是为了防止 [-100, 100] 范围的，val = 0 的时候，直接 return -100 的情况
        if (val === undefined || val === null) return this.props.lowerLimit;

        let newVal = val;
        if (val > this.props.upperLimit) {
            newVal = this.props.upperLimit;
        } else if (val < this.props.lowerLimit) {
            newVal = this.props.lowerLimit;
        }

        if (Number.isInteger(newVal)) {
            return newVal;
        }
        newVal = parseFloat(newVal.toFixed(this.props.toFixed));
        return newVal;
    };

    /**
     * 当拖住鼠标不放，保持改变滑动条的进度值
     * @param {string} value 滑动条当前进度值
     * @param {bool} dragEnd 当前是否拖拽鼠标是否结束，结束就是true，否则就是false/undefined
     */
    private onSlideBarChanging = (value) => {
        this.readyToChangeValue(this.props.slideBarStep ? parseFloat(value) : parseInt(value, 10), true);
    };

    /**
     * 当改变数字输入框的数值
     * @param {string} value 输入的数值
     * @param {bool} focus 输入框是否聚焦，聚焦就是true，否则就是false/undefined
     */
    private onNumberInputChange = (value) => {
        const v = parseFloat(value);
        const notNumber = value ? isNaN(v) : true;
        this.setState({
            valueNum: notNumber ? 0 : v,
            valueStr: value,
        });
        let changedValue: number|string = notNumber ? 0 : v;
        if (this.checkIsEmptyValue(value)) {
            changedValue = '';
        }
        this.props.onValueChange(this.type, changedValue, false, false);
    };

    /**
     * 当鼠标拖拽Slider结束(e.g. MouseUp)触发
     * @param {string} value
     */
    private onSlideBarChangeEnd = (value) => {
        this.readyToChangeValue(this.props.slideBarStep ? parseFloat(value) : parseInt(value, 10), false);
        this.props.onPreviewEnd?.();
    };

    /**
     * 改变当前组件的状态：Slider(persent), NumberInput(value)
     * @param {number} value
     * @param {bool} onlyChangeUI
     */
    private readyToChangeValue = (value, onlyChangeUI) => {
        value = this.integerValueOfStep(value);
        this.setNewNumSlideBarState(value);
        this.props.onValueChange(this.type, value, false, onlyChangeUI);
    };

    private integerValueOfStep = (value) => {
        if (!this.props.isIntegerOfStep) return value;
        // 为了让滑块拖动后的数值为step的整数倍，避免0.51,9.51等类似数值的出现；
        let tempValue = value;

        const step = this.props.slideBarStep || 0.5;
        if (value < 1) {
            tempValue = Math.round(value / step) * step;
        } else {
            tempValue = Math.floor(value / step) * step;
        }

        return tempValue;
    };

    private setNewNumSlideBarState = (v) => {
        if (this.checkIsEmptyValue(v)) {
            this.setState({
                valueNum: this.props.lowerLimit,
                valueStr: this.numberInputRef?.hasFocus ? String(v) : `${this.props.lowerLimit}${this.postFix}`,
            });
        } else {
            this.setState({
                valueNum: v,
                valueStr: this.numberInputRef?.hasFocus ? String(v) : `${v}${this.postFix}`,
            });
        }
    };

    private handleBlur = () => {
        if (this.postFix && this.state.valueStr.indexOf(this.postFix) === -1) {
            this.setState({
                valueStr: `${this.state.valueNum}${this.postFix}`,
            });
        }
        this.props.onBlur?.();
    };

    private checkIsEmptyValue = value => this.props.canShowEmptyString && value === '';
}
